import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useUserPlan } from "@/hooks/useUserPlan";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  ArrowLeft,
  Plus,
  MoreVertical,
  Edit,
  Trash2,
  Eye,
  EyeOff,
  Shield,
  LayoutTemplate,
  Loader2,
  ChevronUp,
  ChevronDown,
} from "lucide-react";

const fieldTypeLabels: Record<string, string> = {
  email: "Email",
  phone: "Telefone",
  full_name: "Nome Completo",
  first_name: "Primeiro Nome",
  job_title: "Cargo",
  company: "Empresa",
  custom_text: "Texto Personalizado",
  custom_select: "Seleção Personalizada",
};

interface TemplateField {
  field_type: string;
  label: string;
  placeholder?: string;
  options?: string[];
  is_required: boolean;
  sort_order: number;
}

interface Template {
  id: string;
  title: string;
  description: string;
  icon_name: string;
  color_primary: string;
  color_secondary: string;
  color_accent: string;
  color_text_light: string;
  color_text_dark: string;
  ai_question_count: number;
  ai_example_questions: string[];
  ai_final_prompt: string;
  thank_you_message: string;
  fields: TemplateField[];
  is_active: boolean;
  sort_order: number;
}

const emptyTemplate: Omit<Template, "id"> = {
  title: "",
  description: "",
  icon_name: "Briefcase",
  color_primary: "#6366f1",
  color_secondary: "#f1f5f9",
  color_accent: "#10b981",
  color_text_light: "#ffffff",
  color_text_dark: "#1e293b",
  ai_question_count: 5,
  ai_example_questions: [""],
  ai_final_prompt: "",
  thank_you_message: "Obrigado por completar o formulário!",
  fields: [{ field_type: "email", label: "Email", placeholder: "seu@email.com", is_required: true, sort_order: 0 }],
  is_active: true,
  sort_order: 0,
};

export default function AdminTemplates() {
  const { loading: authLoading, isAuthenticated } = useAuth();
  const { role, isLoading: planLoading } = useUserPlan();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [editingTemplate, setEditingTemplate] = useState<Template | null>(null);
  const [isCreating, setIsCreating] = useState(false);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate("/auth");
    }
  }, [authLoading, isAuthenticated, navigate]);

  useEffect(() => {
    if (!authLoading && !planLoading && isAuthenticated && role !== "admin") {
      toast({
        title: "Acesso negado",
        description: "Você não tem permissão para acessar esta página.",
        variant: "destructive",
      });
      navigate("/dashboard");
    }
  }, [authLoading, planLoading, isAuthenticated, role, navigate, toast]);

  useEffect(() => {
    if (isAuthenticated && role === "admin") {
      fetchTemplates();
    }
  }, [isAuthenticated, role]);

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("templates")
        .select("*")
        .order("sort_order");

      if (error) throw error;

      // Cast fields from Json to TemplateField[]
      const parsed = (data || []).map((t) => ({
        ...t,
        fields: (t.fields as unknown as TemplateField[]) || [],
      }));

      setTemplates(parsed);
    } catch (error) {
      console.error("Error fetching templates:", error);
      toast({ title: "Erro ao carregar templates", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!editingTemplate) return;

    if (!editingTemplate.title.trim() || !editingTemplate.ai_final_prompt.trim()) {
      toast({ title: "Preencha título e prompt final", variant: "destructive" });
      return;
    }

    setSaving(true);
    try {
      const payload = {
        title: editingTemplate.title,
        description: editingTemplate.description,
        icon_name: editingTemplate.icon_name,
        color_primary: editingTemplate.color_primary,
        color_secondary: editingTemplate.color_secondary,
        color_accent: editingTemplate.color_accent,
        color_text_light: editingTemplate.color_text_light,
        color_text_dark: editingTemplate.color_text_dark,
        ai_question_count: editingTemplate.ai_question_count,
        ai_example_questions: editingTemplate.ai_example_questions.filter((q) => q.trim()),
        ai_final_prompt: editingTemplate.ai_final_prompt,
        thank_you_message: editingTemplate.thank_you_message,
        fields: JSON.parse(JSON.stringify(editingTemplate.fields)),
        is_active: editingTemplate.is_active,
        sort_order: editingTemplate.sort_order,
      };

      if (isCreating) {
        const { error } = await supabase.from("templates").insert([payload] as any);
        if (error) throw error;
        toast({ title: "Template criado com sucesso!" });
      } else {
        const { error } = await supabase
          .from("templates")
          .update(payload as any)
          .eq("id", editingTemplate.id);
        if (error) throw error;
        toast({ title: "Template atualizado!" });
      }

      setEditingTemplate(null);
      setIsCreating(false);
      fetchTemplates();
    } catch (error) {
      console.error("Error saving template:", error);
      toast({ title: "Erro ao salvar template", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Tem certeza que deseja excluir este template?")) return;

    try {
      const { error } = await supabase.from("templates").delete().eq("id", id);
      if (error) throw error;
      toast({ title: "Template excluído" });
      fetchTemplates();
    } catch (error) {
      console.error("Error deleting template:", error);
      toast({ title: "Erro ao excluir template", variant: "destructive" });
    }
  };

  const handleToggleActive = async (id: string, currentActive: boolean) => {
    try {
      const { error } = await supabase
        .from("templates")
        .update({ is_active: !currentActive })
        .eq("id", id);
      if (error) throw error;
      toast({ title: currentActive ? "Template desativado" : "Template ativado" });
      fetchTemplates();
    } catch (error) {
      console.error("Error toggling template:", error);
      toast({ title: "Erro ao atualizar template", variant: "destructive" });
    }
  };

  const openCreateDialog = () => {
    setEditingTemplate({ id: "", ...emptyTemplate });
    setIsCreating(true);
  };

  const openEditDialog = (template: Template) => {
    setEditingTemplate(template);
    setIsCreating(false);
  };

  if (authLoading || planLoading || role !== "admin") {
    return (
      <div className="min-h-screen bg-gradient-hero flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-hero">
      {/* Header */}
      <header className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" asChild>
              <Link to="/admin">
                <ArrowLeft className="h-4 w-4" />
              </Link>
            </Button>
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 bg-gradient-primary rounded-xl flex items-center justify-center">
                <LayoutTemplate className="h-4 w-4 text-primary-foreground" />
              </div>
              <span className="text-xl font-display font-bold">Gerenciar Templates</span>
            </div>
          </div>
          <Button onClick={openCreateDialog} className="bg-gradient-primary hover:opacity-90">
            <Plus className="mr-2 h-4 w-4" />
            Novo Template
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8 pb-[100px]">
        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-16 w-full" />
            ))}
          </div>
        ) : templates.length === 0 ? (
          <Card className="text-center py-16">
            <CardContent>
              <LayoutTemplate className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-xl font-semibold mb-2">Nenhum template</h3>
              <p className="text-muted-foreground mb-4">Crie seu primeiro template para os usuários.</p>
              <Button onClick={openCreateDialog}>
                <Plus className="mr-2 h-4 w-4" />
                Criar Template
              </Button>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardHeader>
              <CardTitle>Templates Disponíveis</CardTitle>
              <CardDescription>Templates que os usuários podem usar para criar formulários</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Título</TableHead>
                    <TableHead>Descrição</TableHead>
                    <TableHead>Campos</TableHead>
                    <TableHead>Perguntas IA</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="w-[50px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {templates.map((template) => (
                    <TableRow key={template.id}>
                      <TableCell className="font-medium">{template.title}</TableCell>
                      <TableCell className="text-muted-foreground max-w-[200px] truncate">
                        {template.description}
                      </TableCell>
                      <TableCell>{template.fields?.length || 0}</TableCell>
                      <TableCell>{template.ai_question_count}</TableCell>
                      <TableCell>
                        <Badge variant={template.is_active ? "default" : "secondary"}>
                          {template.is_active ? "Ativo" : "Inativo"}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => openEditDialog(template)}>
                              <Edit className="mr-2 h-4 w-4" />
                              Editar
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleToggleActive(template.id, template.is_active)}>
                              {template.is_active ? (
                                <>
                                  <EyeOff className="mr-2 h-4 w-4" />
                                  Desativar
                                </>
                              ) : (
                                <>
                                  <Eye className="mr-2 h-4 w-4" />
                                  Ativar
                                </>
                              )}
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              className="text-destructive"
                              onClick={() => handleDelete(template.id)}
                            >
                              <Trash2 className="mr-2 h-4 w-4" />
                              Excluir
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}
      </main>

      {/* Edit/Create Dialog */}
      <Dialog open={!!editingTemplate} onOpenChange={() => setEditingTemplate(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto pb-[100px]">
          <DialogHeader>
            <DialogTitle>{isCreating ? "Criar Template" : "Editar Template"}</DialogTitle>
            <DialogDescription>
              Configure o template que os usuários poderão usar para criar formulários.
            </DialogDescription>
          </DialogHeader>

          {editingTemplate && (
            <div className="space-y-4 py-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label>Título *</Label>
                  <Input
                    value={editingTemplate.title}
                    onChange={(e) =>
                      setEditingTemplate({ ...editingTemplate, title: e.target.value })
                    }
                    placeholder="Nome do template"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Ícone</Label>
                  <Input
                    value={editingTemplate.icon_name}
                    onChange={(e) =>
                      setEditingTemplate({ ...editingTemplate, icon_name: e.target.value })
                    }
                    placeholder="Briefcase, Heart, GraduationCap..."
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Descrição</Label>
                <Textarea
                  value={editingTemplate.description}
                  onChange={(e) =>
                    setEditingTemplate({ ...editingTemplate, description: e.target.value })
                  }
                  placeholder="Descrição do template..."
                  rows={2}
                />
              </div>

              <div className="grid gap-4 sm:grid-cols-3">
                <div className="space-y-2">
                  <Label>Cor primária</Label>
                  <div className="flex gap-2">
                    <Input
                      type="color"
                      value={editingTemplate.color_primary}
                      onChange={(e) =>
                        setEditingTemplate({ ...editingTemplate, color_primary: e.target.value })
                      }
                      className="w-12 h-10 p-1"
                    />
                    <Input
                      value={editingTemplate.color_primary}
                      onChange={(e) =>
                        setEditingTemplate({ ...editingTemplate, color_primary: e.target.value })
                      }
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Cor secundária</Label>
                  <div className="flex gap-2">
                    <Input
                      type="color"
                      value={editingTemplate.color_secondary}
                      onChange={(e) =>
                        setEditingTemplate({ ...editingTemplate, color_secondary: e.target.value })
                      }
                      className="w-12 h-10 p-1"
                    />
                    <Input
                      value={editingTemplate.color_secondary}
                      onChange={(e) =>
                        setEditingTemplate({ ...editingTemplate, color_secondary: e.target.value })
                      }
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Cor de destaque</Label>
                  <div className="flex gap-2">
                    <Input
                      type="color"
                      value={editingTemplate.color_accent}
                      onChange={(e) =>
                        setEditingTemplate({ ...editingTemplate, color_accent: e.target.value })
                      }
                      className="w-12 h-10 p-1"
                    />
                    <Input
                      value={editingTemplate.color_accent}
                      onChange={(e) =>
                        setEditingTemplate({ ...editingTemplate, color_accent: e.target.value })
                      }
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Nº de perguntas da IA</Label>
                <Input
                  type="number"
                  min={1}
                  max={20}
                  value={editingTemplate.ai_question_count}
                  onChange={(e) =>
                    setEditingTemplate({
                      ...editingTemplate,
                      ai_question_count: parseInt(e.target.value) || 5,
                    })
                  }
                />
              </div>

              <div className="space-y-2">
                <Label>Prompt final para gerar PDF *</Label>
                <Textarea
                  value={editingTemplate.ai_final_prompt}
                  onChange={(e) =>
                    setEditingTemplate({ ...editingTemplate, ai_final_prompt: e.target.value })
                  }
                  placeholder="Instruções para a IA gerar o PDF..."
                  rows={4}
                />
              </div>

              <div className="space-y-2">
                <Label>Mensagem de agradecimento</Label>
                <Textarea
                  value={editingTemplate.thank_you_message}
                  onChange={(e) =>
                    setEditingTemplate({ ...editingTemplate, thank_you_message: e.target.value })
                  }
                  placeholder="Obrigado por completar o formulário!"
                  rows={2}
                />
              </div>

              {/* Fixed Fields Section */}
              <div className="space-y-4 border-t pt-4">
                <div className="flex items-center justify-between">
                  <Label className="text-base font-semibold">Campos Fixos</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const newField: TemplateField = {
                        field_type: "custom_text",
                        label: "",
                        placeholder: "",
                        is_required: true,
                        sort_order: editingTemplate.fields.length,
                      };
                      setEditingTemplate({
                        ...editingTemplate,
                        fields: [...editingTemplate.fields, newField],
                      });
                    }}
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Adicionar Campo
                  </Button>
                </div>

                {editingTemplate.fields.length === 0 && (
                  <p className="text-sm text-muted-foreground">Nenhum campo adicionado</p>
                )}

                {editingTemplate.fields.map((field, index) => (
                  <div key={index} className="border rounded-lg p-4 space-y-3 bg-muted/30">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">Campo {index + 1}</span>
                      <div className="flex items-center gap-1">
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          disabled={index === 0}
                          onClick={() => {
                            const newFields = [...editingTemplate.fields];
                            [newFields[index - 1], newFields[index]] = [newFields[index], newFields[index - 1]];
                            newFields.forEach((f, i) => (f.sort_order = i));
                            setEditingTemplate({ ...editingTemplate, fields: newFields });
                          }}
                        >
                          <ChevronUp className="h-4 w-4" />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          disabled={index === editingTemplate.fields.length - 1}
                          onClick={() => {
                            const newFields = [...editingTemplate.fields];
                            [newFields[index], newFields[index + 1]] = [newFields[index + 1], newFields[index]];
                            newFields.forEach((f, i) => (f.sort_order = i));
                            setEditingTemplate({ ...editingTemplate, fields: newFields });
                          }}
                        >
                          <ChevronDown className="h-4 w-4" />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-destructive hover:text-destructive"
                          onClick={() => {
                            const newFields = editingTemplate.fields.filter((_, i) => i !== index);
                            newFields.forEach((f, i) => (f.sort_order = i));
                            setEditingTemplate({ ...editingTemplate, fields: newFields });
                          }}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>

                    <div className="grid gap-3 sm:grid-cols-2">
                      <div className="space-y-1">
                        <Label className="text-xs">Tipo</Label>
                        <Select
                          value={field.field_type}
                          onValueChange={(value) => {
                            const newFields = [...editingTemplate.fields];
                            newFields[index] = { ...field, field_type: value };
                            setEditingTemplate({ ...editingTemplate, fields: newFields });
                          }}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {Object.entries(fieldTypeLabels).map(([key, label]) => (
                              <SelectItem key={key} value={key}>
                                {label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Label</Label>
                        <Input
                          value={field.label}
                          onChange={(e) => {
                            const newFields = [...editingTemplate.fields];
                            newFields[index] = { ...field, label: e.target.value };
                            setEditingTemplate({ ...editingTemplate, fields: newFields });
                          }}
                          placeholder="Nome do campo"
                        />
                      </div>
                    </div>

                    <div className="grid gap-3 sm:grid-cols-2">
                      <div className="space-y-1">
                        <Label className="text-xs">Placeholder</Label>
                        <Input
                          value={field.placeholder || ""}
                          onChange={(e) => {
                            const newFields = [...editingTemplate.fields];
                            newFields[index] = { ...field, placeholder: e.target.value };
                            setEditingTemplate({ ...editingTemplate, fields: newFields });
                          }}
                          placeholder="Texto de ajuda"
                        />
                      </div>
                      <div className="flex items-center gap-2 pt-5">
                        <Switch
                          checked={field.is_required}
                          onCheckedChange={(checked) => {
                            const newFields = [...editingTemplate.fields];
                            newFields[index] = { ...field, is_required: checked };
                            setEditingTemplate({ ...editingTemplate, fields: newFields });
                          }}
                        />
                        <Label className="text-xs">Obrigatório</Label>
                      </div>
                    </div>

                    {field.field_type === "custom_select" && (
                      <div className="space-y-1">
                        <Label className="text-xs">Opções (uma por linha)</Label>
                        <Textarea
                          value={(field.options || []).join("\n")}
                          onChange={(e) => {
                            const newFields = [...editingTemplate.fields];
                            newFields[index] = {
                              ...field,
                              options: e.target.value.split("\n").filter((o) => o.trim()),
                            };
                            setEditingTemplate({ ...editingTemplate, fields: newFields });
                          }}
                          rows={3}
                          placeholder="Opção 1&#10;Opção 2&#10;Opção 3"
                        />
                      </div>
                    )}
                  </div>
                ))}
              </div>

              <div className="flex items-center gap-3">
                <Switch
                  checked={editingTemplate.is_active}
                  onCheckedChange={(checked) =>
                    setEditingTemplate({ ...editingTemplate, is_active: checked })
                  }
                />
                <Label>Template ativo</Label>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingTemplate(null)}>
              Cancelar
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Salvando...
                </>
              ) : (
                "Salvar"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
